import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, MapPin, Instagram, Linkedin, Youtube } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const SOCIAL_LINKS = [
  { label: "Instagram", href: "https://www.instagram.com/ashlarbuildchem", Icon: Instagram },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/ashlarbuildchem", Icon: Linkedin },
  { label: "YouTube", href: "https://www.youtube.com/@ashlarbuildchem", Icon: Youtube },
];

export default function Footer() {
  const { t } = useLanguage();

  const COLS = [
    {
      titleKey: "footer.products",
      links: [
        { labelKey: "footer.tileAdhesives", to: "/products?cat=tile-adhesives" },
        { labelKey: "footer.grouts", to: "/products?cat=grouts" },
        { labelKey: "footer.admixtures", to: "/products?cat=admixtures" },
        { labelKey: "footer.blockAdhesive", to: "/products?cat=block-adhesive" },
        { labelKey: "footer.plasterFlooring", to: "/products?cat=surface-solutions" },
      ],
    },
    {
      titleKey: "footer.tools",
      links: [
        { labelKey: "footer.adhesiveCalculator", to: "/calculators?tool=adhesive" },
        { labelKey: "footer.groutCalculator", to: "/calculators?tool=grout" },
        { labelKey: "footer.groutVisualiser", to: "/grout-visualizer" },
        { labelKey: "footer.journal", to: "/blog" },
      ],
    },
    {
      titleKey: "footer.company",
      links: [
        { labelKey: "footer.aboutUs", to: "/about" },
        { labelKey: "footer.careers", to: "/careers" },
        { labelKey: "footer.contact", to: "/contact" },
        { labelKey: "footer.getQuote", to: "/contact" },
      ],
    },
  ];

  return (
    <footer className="relative border-t border-black/10 bg-background" data-testid="main-footer">
      <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-10">
        <div className="grid gap-12 border-b border-black/10 pb-16 md:grid-cols-[1.4fr_1fr]">
          <div>
            <span className="label text-primary">{t("footer.letsBuild")}</span>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-[0.95] tracking-tight md:text-6xl">
              {t("footer.certifiedQuality")}<br />{t("footer.modernIndia")}
            </h2>
          </div>
          <div className="flex flex-col justify-end gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-between border border-black/15 px-6 py-5 transition-colors duration-200 hover:bg-primary hover:text-primary-foreground"
              data-testid="footer-quote-cta"
            >
              <span className="font-display text-lg font-medium">{t("footer.requestQuote")}</span>
              <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:rotate-45" />
            </Link>
            <div className="grid grid-cols-1 gap-3 text-sm text-muted-foreground sm:grid-cols-1">
              <a href="tel:18003090808" className="flex items-center gap-3 hover:text-foreground" data-testid="footer-phone">
                <Phone className="h-4 w-4 text-primary" /> 1800 309 0808
              </a>
              <a href="mailto:care@ashlarbuildchem.com" className="flex min-w-0 items-center gap-3 hover:text-foreground" data-testid="footer-email">
                <Mail className="h-4 w-4 shrink-0 text-primary" /> <span className="min-w-0 break-all">care@ashlarbuildchem.com</span>
              </a>
              <span className="flex min-w-0 items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <span className="min-w-0 break-words">247(NE), Scheme 4C, Jamunapuri, Jaipur — 302039, Rajasthan</span>
              </span>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  title={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="inline-flex h-9 w-9 items-center justify-center border border-black/15 transition-colors duration-200 hover:border-primary hover:text-primary"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-10 py-16 md:grid-cols-4">
          <div className="max-w-xs">
            <p className="label text-primary">{t("footer.isoLine")}</p>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              {t("footer.blurb")}
            </p>
          </div>
          {COLS.map((col) => (
            <div key={col.titleKey}>
              <h4 className="label text-muted-foreground">{t(col.titleKey)}</h4>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.labelKey}>
                    <Link
                      to={l.to}
                      className="text-sm text-foreground/90 transition-colors duration-200 hover:text-primary"
                    >
                      {t(l.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto flex max-w-[1600px] flex-col items-center justify-between gap-2 border-t border-black/10 px-6 py-6 text-xs text-muted-foreground md:flex-row md:px-10">
        <span>© {new Date().getFullYear()} {t("footer.rights")}</span>
        <span className="label">{t("footer.tagline")}</span>
      </div>
    </footer>
  );
}
